import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Input, message } from 'antd';
import Tooltip from '../../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import { useUpdateCustomTextVariable } from '../../../hooks'
import ButtonCommon from '../../common/ButtonCommon'
import { antdFormError, getPrimaryDark } from '../../../constants/constants'

// Valeurs prédéfinies d'une variable texte custom : stockées en base sur la
// variable (colonne predefined_values), donc partagées par tous les documents.
// Elles sont proposées à l'utilisateur dans l'aperçu du formulaire, à côté du
// bouton d'information (voir PredefinedValuesPicker).
//
// L'état des champs est local : le store n'est écrit qu'au blur / à l'ajout /
// à la suppression, sinon une ligne encore vide serait effacée en cours de
// saisie par la resynchronisation depuis le store.
// Le champ n'accepte que du texte : on écarte ce qui ne serait pas une chaîne
// (contenu hérité, réponse API inattendue) plutôt que de le donner à <Input>.
const toStringList = (values) => (values ?? []).filter((value) => typeof value === 'string')

const FormPredefinedValues = ({ box, disabled = false }) => {
  const { t } = useTranslation()
  const accentColor = useStore(({ currentDocument }) => getPrimaryDark(currentDocument?.type === 'template'))
  const updateVariable = useStore(({ updateVariable }) => updateVariable)
  const storedValues = useStore(({ variables }) => variables[box.id]?.predefinedValues)
  const [values, setValues] = useState(() => toStringList(storedValues))

  const { mutate: updateCustomTextVariable } = useUpdateCustomTextVariable()

  // Resynchroniser seulement au changement de variable (le remontage n'est pas
  // garanti selon l'endroit d'où l'éditeur est rendu).
  const storedValuesRef = useRef(storedValues)
  storedValuesRef.current = storedValues
  const boxId = box.id
  useEffect(() => {
    setValues(toStringList(storedValuesRef.current))
  }, [boxId])

  const persist = useCallback((nextValues) => {
    const cleaned = [...new Set(nextValues.map((v) => v.trim()).filter((v) => v !== ''))]
    const previous = toStringList(storedValuesRef.current)
    if (cleaned.length === previous.length && cleaned.every((v, i) => v === previous[i])) return

    const id = boxId.substring(boxId.lastIndexOf('.') + 1)
    updateCustomTextVariable({ id, predefinedValues: cleaned }, {
      onSuccess: () => {
        updateVariable(boxId, (variable) => {
          variable.predefinedValues = cleaned
        })
      },
      // Sans ça les champs afficheraient une liste que la base n'a pas enregistrée.
      onError: () => {
        message.error({ content: t('predefined-values-save-error'), key: 'predefined-values-save' })
        setValues(toStringList(storedValuesRef.current))
      }
    })
  }, [boxId, updateCustomTextVariable, updateVariable, t])

  const handleChange = (index, value) => {
    setValues((prev) => prev.map((v, i) => (i === index ? value : v)))
  }

  const handleDelete = (index) => {
    const next = values.filter((_, i) => i !== index)
    setValues(next)
    persist(next)
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 8 }}>{t('predefined-values')}</div>
      {/* Liste bornée puis scrollable : sans ça une longue liste repoussait le
          bouton de suppression de la variable hors de la carte.
          paddingRight : l'icône de suppression ne passe pas sous l'ascenseur. */}
      <div style={{ maxHeight: 220, overflowY: 'auto', paddingRight: values.length > 4 ? 4 : 0 }}>
        {values.map((value, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Input
              value={value}
              disabled={disabled}
              autoFocus={value === ''}
              placeholder={t('predefined-value-placeholder')}
              onChange={(e) => handleChange(index, e.target.value)}
              onBlur={() => persist(values)}
              onPressEnter={(e) => e.currentTarget.blur()}
            />
            {!disabled && (
              <Tooltip title={t('delete')} mouseLeaveDelay="0">
                <FontAwesomeIcon
                  icon={icon({ name: 'trash-xmark', style: 'duotone' })}
                  onClick={() => handleDelete(index)}
                  style={{ cursor: 'pointer', color: antdFormError }}
                />
              </Tooltip>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <ButtonCommon
          type="add"
          accentColor={accentColor}
          onClick={() => setValues((prev) => [...prev, ''])}
        >
          {t('add-predefined-value')}
        </ButtonCommon>
      )}
    </div>
  )
}

export default FormPredefinedValues
